import { useCallback, useMemo, useState } from "react";
import { AssetTypes } from "@zapier/utils";
import { useTranslations } from "@zapier/i18n";
import type { BulkSelectAction } from "../components/common/Table/BulkActions/BulkActions";
import {
  useEmitZapBulkActionEvent,
  ZapBulkActionOptions,
} from "../events/useEmitZapBulkActionEvent";

interface Input {
  assetType?: AssetTypes;
  onMoveClicked?: (typedIds: string[]) => void;
  onCopyToAnotherAccountClicked?: (typedIds: string[]) => void;
  onDeleteClicked?: (typedIds: string[]) => void;
  onChangeOwnerClicked?: (typedIds: string[]) => void;
  isBulkMoveEnabled?: boolean;
  isBulkCopyEnabled?: boolean;
  isBulkDeleteEnabled?: boolean;
  isBulkChangeOwnerEnabled?: boolean;
  // Live, selection-driven state for the bulk-delete button: disables it (and
  // shows a tooltip) once a reactive permission check finds 0 deletable ids.
  isDeleteActionDisabled?: boolean;
  deleteActionTooltip?: string;
}

/**
 * Provides bulk select actions and move-modal state for asset list pages.
 *
 * When `assetType` is provided, selected row IDs are prefixed (e.g. "zap:123").
 * When omitted, IDs are assumed to already be typed (for mixed-type lists like Favorites).
 *
 * Must be rendered inside a DataTableProvider (TableProvider).
 */
export function useAssetListBulkActions({
  assetType,
  onMoveClicked,
  onCopyToAnotherAccountClicked,
  onDeleteClicked,
  onChangeOwnerClicked,
  // Preserve existing move-action behavior for existing list pages by default.
  isBulkMoveEnabled = true,
  // Keep copy action opt-in until a page explicitly wires copy candidates/modal.
  isBulkCopyEnabled = false,
  isBulkDeleteEnabled = false,
  isBulkChangeOwnerEnabled = false,
  isDeleteActionDisabled,
  deleteActionTooltip,
}: Input) {
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isChangeOwnerModalOpen, setIsChangeOwnerModalOpen] = useState(false);
  const [selectedTypedAssetIds, setSelectedTypedAssetIds] = useState<string[]>(
    [],
  );
  const [selectedTypedAssetIdsForCopy, setSelectedTypedAssetIdsForCopy] =
    useState<string[]>([]);
  const { emit } = useEmitZapBulkActionEvent();
  const tAction = useTranslations("buttons");

  const handleCloseMove = useCallback(() => {
    setIsMoveModalOpen(false);
    setSelectedTypedAssetIds([]);
  }, []);

  const handleCloseDelete = useCallback(() => {
    setIsDeleteModalOpen(false);
    setSelectedTypedAssetIds([]);
  }, []);

  const handleCloseChangeOwner = useCallback(() => {
    setIsChangeOwnerModalOpen(false);
    setSelectedTypedAssetIds([]);
  }, []);

  const handleCloseCopy = useCallback(() => {
    setIsCopyModalOpen(false);
    setSelectedTypedAssetIdsForCopy([]);
  }, []);

  const bulkSelectActions = useMemo<BulkSelectAction[]>(() => {
    const actions: BulkSelectAction[] = [];

    if (isBulkMoveEnabled) {
      actions.push({
        id: "bulk-move-to-folder",
        onClick: (ids: string[]) => {
          const typedIds = assetType
            ? ids.map((id) => `${assetType}:${id}`)
            : ids;
          setSelectedTypedAssetIds(typedIds);
          onMoveClicked?.(typedIds);
          setIsMoveModalOpen(true);
          // TODO (ASM-4152): This emits a Zap-specific event for all asset
          // types. Extend the event schema to include the actual asset type
          // so analytics can distinguish Canvas/Table/Chatbot bulk moves.
          emit({ optionSelected: ZapBulkActionOptions.Move });
        },
        text: tAction("move-to-folder"),
        icon: "actionMoveToFolder",
      });
    }

    if (isBulkCopyEnabled) {
      actions.push({
        id: "bulk-copy-to-another-account",
        onClick: (ids: string[]) => {
          const typedIds = assetType
            ? ids.map((id) => `${assetType}:${id}`)
            : ids;
          setSelectedTypedAssetIdsForCopy(typedIds);
          onCopyToAnotherAccountClicked?.(typedIds);
          setIsCopyModalOpen(true);
        },
        text: tAction("copy-to-another-account"),
        icon: "arrowExit",
        isVisible: (selectedRows) => selectedRows.length > 0,
      });
    }

    if (isBulkChangeOwnerEnabled) {
      actions.push({
        id: "bulk-change-owner",
        onClick: (ids: string[]) => {
          const typedIds = assetType
            ? ids.map((id) => `${assetType}:${id}`)
            : ids;
          setSelectedTypedAssetIds(typedIds);
          onChangeOwnerClicked?.(typedIds);
          setIsChangeOwnerModalOpen(true);
          emit({ optionSelected: ZapBulkActionOptions.ChangeOwner });
        },
        text: tAction("change-owner"),
        icon: "personSettings",
      });
    }

    if (isBulkDeleteEnabled) {
      actions.push({
        id: "bulk-delete",
        onClick: (ids: string[]) => {
          const typedIds = assetType
            ? ids.map((id) => `${assetType}:${id}`)
            : ids;
          setSelectedTypedAssetIds(typedIds);
          onDeleteClicked?.(typedIds);
          setIsDeleteModalOpen(true);
          emit({ optionSelected: ZapBulkActionOptions.Delete });
        },
        text: tAction("move-to-trash"),
        icon: "actionTrash",
        disabled: isDeleteActionDisabled,
        tooltipText: deleteActionTooltip,
      });
    }

    return actions;
  }, [
    assetType,
    deleteActionTooltip,
    emit,
    isBulkCopyEnabled,
    isBulkDeleteEnabled,
    isBulkChangeOwnerEnabled,
    isBulkMoveEnabled,
    isDeleteActionDisabled,
    onChangeOwnerClicked,
    onCopyToAnotherAccountClicked,
    onDeleteClicked,
    onMoveClicked,
    tAction,
  ]);

  const setMoveModalOpen = useCallback(
    (isOpen: boolean) => {
      if (isOpen) setIsMoveModalOpen(true);
      else handleCloseMove();
    },
    [handleCloseMove],
  );

  const setCopyModalOpen = useCallback(
    (isOpen: boolean) => {
      if (isOpen) setIsCopyModalOpen(true);
      else handleCloseCopy();
    },
    [handleCloseCopy],
  );

  const setDeleteModalOpen = useCallback(
    (isOpen: boolean) => {
      if (isOpen) setIsDeleteModalOpen(true);
      else handleCloseDelete();
    },
    [handleCloseDelete],
  );

  const setChangeOwnerModalOpen = useCallback(
    (isOpen: boolean) => {
      if (isOpen) setIsChangeOwnerModalOpen(true);
      else handleCloseChangeOwner();
    },
    [handleCloseChangeOwner],
  );

  return {
    bulkSelectActions,
    isMoveModalOpen,
    setMoveModalOpen,
    selectedTypedAssetIds,
    onMoveSettled: handleCloseMove,
    isCopyModalOpen,
    setCopyModalOpen,
    selectedTypedAssetIdsForCopy,
    onCopySettled: handleCloseCopy,
    isDeleteModalOpen,
    setDeleteModalOpen,
    onDeleteSettled: handleCloseDelete,
    isChangeOwnerModalOpen,
    setChangeOwnerModalOpen,
    onChangeOwnerSettled: handleCloseChangeOwner,
  };
}
